import {
  DottedText,
  Edit,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import {
  decryptData,
  formatDate,
  getCareNavigatorLabel,
  numberToCurrencyFormatter,
} from './helper';
import { Link } from 'react-router-dom';
import { PATIENT_CASES } from '../routes';
import { Interaction } from '@HomaHealth/ui-component-library/lib/_AIQ-components/molecules/InteractionCard';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import moment from 'moment';

export interface PatientCurrentHealthState {
  conditions: string;
  medications: string;
  procedures: string;
}

export interface CareNavigator {
  id: string;
  name: string;
  role: string;
  assignedOn: string;
}

export interface InfoCardItems {
  label: string;
  value: string;
}

export const formatPatientCasesHistory = (
  casesHistory: any[],
  memberId: string,
) => {
  const patientCasesHistory: any[] = [];

  casesHistory.map((item) => {
    const tempHistory = [];

    tempHistory.push(
      <Link
        to={PATIENT_CASES}
        state={{
          memberId: memberId,
          caseId: item?.case_id,
          isCaseClosed: true,
        }}
      >
        <DottedText
          text={item?.case_id}
          typographyStyles={{
            fontSize: '14px',
            lineHeight: '24px',
            fontWeight: 500,
            color: patientAccessTheme.palette.primary[500],
          }}
        />
      </Link>,
    );
    tempHistory.push(
      item?.created_at ? formatDate(item?.created_at, 'MM-DD-YYYY') : '-',
    );
    tempHistory.push(
      item?.closed_at ? formatDate(item?.closed_at, 'MM-DD-YYYY') : '-',
    );
    tempHistory.push(item?.case_type ? item?.case_type : '-');
    tempHistory.push(item?.type_of_trigger ? item?.type_of_trigger : '-');
    tempHistory.push(item?.noOfReferrals ? item?.noOfReferrals : '-');
    tempHistory.push(item?.noOfEngagements ? item?.noOfEngagements : '-');

    patientCasesHistory.push(tempHistory);
  });

  return patientCasesHistory;
};

export const formatCaseDetails = (caseDetails: any) => {
  const conditions = caseDetails[0]?.current_health_state?.conditions || '-';
  const medications = caseDetails[0]?.current_health_state?.medications || '-';
  const procedures = caseDetails[0]?.current_health_state?.procedures || '-';

  return {
    conditions,
    medications,
    procedures,
  };
};
export const formatCareNavigator = (caseDetails: any) => {
  const name = caseDetails[0]?.cn_name || '-';
  const role = caseDetails[0]?.cn_role || '-';
  const assignedOn = caseDetails[0]?.cn_assign_date
    ? formatDate(caseDetails[0].cn_assign_date, 'MM-DD-YYYY')
    : '-';
  const id = caseDetails[0]?.cn_id || '-';

  return {
    id,
    name,
    role,
    assignedOn,
  };
};

export const formatInteractions = (data: any, isCareNavigator: boolean) => {
  const interactions: Interaction[] = [];

  data?.map((item: any) => {
    const interaction: Interaction = {
      date: moment.utc(item?.interaction_date).format('MMMM Do YYYY, h:mm A'),
      mode: item?.mode || '-',
      touchpointDetails: item?.details?.[0] || '',
      interactionType: item?.type === 'Outbound' ? 'outbound' : 'inbound',
      _id: isCareNavigator ? item._id : null,
    };

    if (item?.referral) {
      interaction.referral = item?.referral;
    }
    interactions.push(interaction);
  });
  return interactions;
};

export const formatInfoCard = (data: any, cn: boolean) => {
  if (cn === true) {
    const care_navigator = data as CareNavigator;
    return [
      { label: 'Name', value: care_navigator.name },
      {
        label: 'Role',
        value: getCareNavigatorLabel(care_navigator.role),
      },
      { label: 'Assigned On', value: care_navigator.assignedOn },
    ];
  }

  const case_details = data as PatientCurrentHealthState;
  return [
    { label: 'Conditions', value: case_details.conditions },
    { label: 'Medications', value: case_details.medications },
    { label: 'Recent Procedures', value: case_details?.procedures },
  ];
};
export const caseDetailsLabelMapper = (label: string) => {
  switch (label) {
    case 'Conditions':
      return 'conditions';
    case 'Medications':
      return 'medications';
    case 'Recent Procedures':
      return 'procedures';
    default:
      return '';
  }
};

const getValuesForStatsTable = (
  key: string,
  isValue: boolean,
  isFirst?: boolean,
) => {
  return (
    <Typography
      fontSize="14px"
      fontType="medium"
      lineHeight="22px"
      textColor={
        isFirst
          ? patientAccessTheme.palette.status.white
          : isValue
          ? patientAccessTheme.palette.text.lowemphasis
          : patientAccessTheme.palette.text.highemphasis
      }
    >
      {key}
    </Typography>
  );
};

const formatStatsTable = (statsData: any) => {
  const stats: any[] = [];
  stats.push([
    getValuesForStatsTable('CARE NAVIGATOR', false, true),
    getValuesForStatsTable(
      `${statsData?.careNavName} (${
        statsData?.careNavRole === 'care_navigator_nurse'
          ? 'Nurse'
          : 'Pharmacist'
      })`,
      true,
      true,
    ),
  ]);
  stats.push([
    getValuesForStatsTable('CASE TYPE', false),
    getValuesForStatsTable(statsData?.caseType || '-', true),
  ]);
  stats.push([
    getValuesForStatsTable('PARTNER REFERREL', false),
    getValuesForStatsTable(statsData?.referral || '-', true),
  ]);
  stats.push([
    getValuesForStatsTable('SAVINGS IMPACT', false),
    getValuesForStatsTable(
      statsData?.savings ? numberToCurrencyFormatter(statsData?.savings) : '-',
      true,
    ),
  ]);
  stats.push([
    getValuesForStatsTable('MEMBER RESPONSIVENESS', false),
    getValuesForStatsTable(
      `${statsData?.responsiveness}-"${statsData?.responsiveness_desc}"`,
      true,
    ),
  ]);
  return stats;
};
const formatInteractionData = (
  interactionData: any,
  handleEditInteraction: any,
  isCareNavigator: boolean,
) => {
  const interactions: any[] = [];

  interactionData.map((item: any) => {
    const interaction: any[] = [];
    interaction.push(
      moment.utc(item?.interaction_date).format('MMMM Do YYYY, h:mm A'),
    );
    interaction.push(item?.mode);
    interaction.push(item?.condition || '-');
    interaction.push(item?.details?.join(' '));
    if (isCareNavigator) {
      interaction.push(
        <Edit
          style={{
            height: '22px',
            width: '22px',
            color: patientAccessTheme.palette.primary.main,
          }}
          onClick={() => handleEditInteraction(item)}
        />,
      );
    }

    interactions.push(interaction);
  });
  return interactions;
};
const formatRatingReviewData = (statsData: any) => {
  return {
    rating: statsData?.rating || null,
    review: statsData?.review || '',
    name:
      statsData?.createdBy === 'system'
        ? decryptData(statsData?.name)
        : statsData?.name || '',
    careNavigatorName: statsData?.careNavName || '',
    careNavigatorRole: statsData?.careNavRole || '',
    careNavigatorId: statsData?.careNavId,
  };
};

export const formatEngagementReportData = (
  statsData: any,
  handleEditInteraction: any,
  isCareNavigator: boolean,
) => {
  let engagementData = {};

  engagementData = {
    stats: formatStatsTable(statsData?.stats),
    interaction: formatInteractionData(
      statsData?.iteraction,
      handleEditInteraction,
      isCareNavigator,
    ),
    rating: formatRatingReviewData(statsData?.stats),
  };
  return engagementData;
};
